import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import whiteLogo from '../assets/white-logo.png';
import ConsultationModal from './ConsultationModal';
import {
  COMPANY_NAME,
  COMPANY_LEGAL_NAME,
  COMPANY_NUMBER,
  COMPANY_JURISDICTION,
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  PORTAL_URL,
  SOCIAL_FACEBOOK,
  SOCIAL_INSTAGRAM,
  SOCIAL_LINKEDIN,
} from '../constants';

const Footer = () => {
  const [showConsultationModal, setShowConsultationModal] = useState(false);

  return (
    <footer className="bg-primary text-white">
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            
            {/* Brand Column */}
            <div>
              <div className="mb-6">
                <Link to="/" className="inline-block hover:opacity-80 transition">
                  <img src={whiteLogo} alt={COMPANY_NAME} className="h-8" />
                </Link>
              </div>
              <p className="text-gray-300 text-sm font-light leading-relaxed mb-6">
                Guidance and application support for studying, working and settling abroad.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-300 font-light">
                  <Mail size={16} />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white transition-colors">
                    {CONTACT_EMAIL}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-300 font-light">
                  <Phone size={16} />
                  <a href={`tel:${CONTACT_PHONE_TEL}`} className="hover:text-white transition-colors">
                    {CONTACT_PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>

            {/* Who We Are Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-4">WHO WE ARE</h4>
              <ul className="space-y-2 text-sm text-gray-300 font-light">
                <li>
                  <Link to="/about" className="hover:text-white transition-colors">
                    About {COMPANY_NAME}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-white transition-colors">
                    News & Blog
                  </Link>
                </li>
              </ul>
            </div>

            {/* Our Services Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-4">OUR SERVICES</h4>
              <ul className="space-y-2 text-sm text-gray-300 font-light">
                <li>
                  <Link to="/study-abroad" className="hover:text-white transition-colors">
                    Study Abroad Support
                  </Link>
                </li>
                <li>
                  <Link to="/work-abroad" className="hover:text-white transition-colors">
                    Work Abroad Opportunities
                  </Link>
                </li>
              </ul>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 className="text-base font-semibold text-white mb-4">QUICK LINKS</h4>
              <ul className="space-y-2 text-sm text-gray-300 font-light">
                <li>
                  <button 
                    onClick={() => setShowConsultationModal(true)}
                    className="hover:text-white transition-colors text-left"
                  >
                    Book a Consultation
                  </button>
                </li>
                <li>
                  <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Client Portal Login
                  </a>
                </li>
                <li>
                  <Link to="/pricing" className="hover:text-white transition-colors">
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-600 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              {/* Disclaimer */}
              <div className="text-xs text-gray-400 font-light max-w-2xl">
                <p className="mb-1">
                  © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
                </p>
                <p>
                  {COMPANY_LEGAL_NAME} · Company number {COMPANY_NUMBER} ({COMPANY_JURISDICTION})
                </p>
              </div>

              {/* Social Media */}
              <div className="flex items-center gap-4">
                {(SOCIAL_FACEBOOK || SOCIAL_INSTAGRAM || SOCIAL_LINKEDIN) && (
                  <span className="text-xs text-gray-400 font-light mr-2">Follow us:</span>
                )}
                {SOCIAL_FACEBOOK && (
                  <a href={SOCIAL_FACEBOOK} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="Facebook">
                    <Facebook size={20} />
                  </a>
                )}
                {SOCIAL_INSTAGRAM && (
                  <a href={SOCIAL_INSTAGRAM} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="Instagram">
                    <Instagram size={20} />
                  </a>
                )}
                {SOCIAL_LINKEDIN && (
                  <a href={SOCIAL_LINKEDIN} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors" aria-label="LinkedIn">
                    <Linkedin size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={showConsultationModal} 
        onClose={() => setShowConsultationModal(false)} 
        serviceType="study-abroad"
      />
    </footer>
  );
};

export default Footer;
